import {
  ExclamationTriangleIcon,
  UserCircleIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  ChevronRightIcon,
  MapPinIcon,
  ShieldCheckIcon,
  IdentificationIcon,
} from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';
import type { SearchResult } from '@/services/search.service';

interface SearchResultItemProps {
  result: SearchResult;
  isSelected?: boolean;
  onClick: () => void;
}

const getResultIcon = (type: SearchResult['type']) => {
  switch (type) {
    case 'incident':
      return ExclamationTriangleIcon;
    case 'user':
      return UserCircleIcon;
    case 'threat':
      return EyeIcon;
    case 'investigation':
      return MagnifyingGlassIcon;
    case 'activity':
      return MapPinIcon;
    case 'protectedPerson':
      return ShieldCheckIcon;
    case 'subject':
      return IdentificationIcon;
  }
};

const getResultColor = (type: SearchResult['type']) => {
  switch (type) {
    case 'incident':
      return 'text-danger-600 dark:text-danger-400 bg-danger-50 dark:bg-danger-900/20';
    case 'user':
      return 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20';
    case 'threat':
      return 'text-warning-600 dark:text-warning-400 bg-warning-50 dark:bg-warning-900/20';
    case 'investigation':
      return 'text-secondary-600 dark:text-secondary-400 bg-secondary-50 dark:bg-secondary-900/20';
    case 'activity':
      return 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20';
    case 'protectedPerson':
      return 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20';
    case 'subject':
      return 'text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20';
  }
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

  if (diffInMinutes < 60) { return `${diffInMinutes}m ago`; }
  if (diffInMinutes < 1440) { return `${Math.floor(diffInMinutes / 60)}h ago`; }
  return date.toLocaleDateString();
};

export default function SearchResultItem({ result, isSelected = false, onClick }: SearchResultItemProps) {
  const Icon = getResultIcon(result.type);
  const colorClass = getResultColor(result.type);

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-start gap-3 p-3 rounded-lg transition-colors text-left',
        isSelected
          ? 'bg-primary-50 dark:bg-primary-900/20'
          : 'hover:bg-secondary-50 dark:hover:bg-secondary-800/50'
      )}
    >
      {/* Icon */}
      <div className={cn('p-2 rounded-lg flex-shrink-0', colorClass)}>
        <Icon className="w-5 h-5" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <h4 className="font-medium text-secondary-900 dark:text-white text-sm line-clamp-1">
            {result.title}
          </h4>
          {isSelected && (
            <ChevronRightIcon className="w-4 h-4 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
          )}
        </div>

        {result.description && (
          <p className="text-sm text-secondary-600 dark:text-secondary-400 line-clamp-1 mt-0.5">
            {result.description}
          </p>
        )}

        {/* Metadata */}
        <div className="flex items-center gap-3 mt-1 text-xs text-secondary-500 dark:text-secondary-500">
          <span className="capitalize">{result.type}</span>
          <span>•</span>
          <span>{formatDate(result.createdAt)}</span>

          {/* Additional metadata based on type */}
          {result.metadata && (
            <>
              {result.type === 'incident' && result.metadata.severity && (
                <>
                  <span>•</span>
                  <span className={cn(
                    'uppercase font-medium',
                    (result.metadata.severity as string) === 'CRITICAL' && 'text-danger-600 dark:text-danger-400',
                    (result.metadata.severity as string) === 'HIGH' && 'text-orange-600 dark:text-orange-400',
                    (result.metadata.severity as string) === 'MEDIUM' && 'text-warning-600 dark:text-warning-400',
                    (result.metadata.severity as string) === 'LOW' && 'text-success-600 dark:text-success-400'
                  )}>
                    {result.metadata.severity as string}
                  </span>
                </>
              )}

              {result.type === 'user' && result.metadata.roles && (
                <>
                  <span>•</span>
                  <span>{result.metadata.roles as string}</span>
                </>
              )}

              {result.type === 'threat' && result.metadata.threatLevel && (
                <>
                  <span>•</span>
                  <span className="uppercase">{result.metadata.threatLevel as string}</span>
                </>
              )}

              {result.type === 'investigation' && result.metadata.status && (
                <>
                  <span>•</span>
                  <span className="uppercase">{result.metadata.status as string}</span>
                </>
              )}

              {result.type === 'activity' && result.metadata.status && (
                <>
                  <span>•</span>
                  <span className="uppercase">{result.metadata.status as string}</span>
                </>
              )}

              {result.type === 'protectedPerson' && result.metadata.riskLevel && (
                <>
                  <span>•</span>
                  <span className={cn(
                    'uppercase font-medium',
                    (result.metadata.riskLevel as string) === 'critical' && 'text-danger-600 dark:text-danger-400',
                    (result.metadata.riskLevel as string) === 'high' && 'text-orange-600 dark:text-orange-400',
                    (result.metadata.riskLevel as string) === 'medium' && 'text-warning-600 dark:text-warning-400',
                    (result.metadata.riskLevel as string) === 'low' && 'text-success-600 dark:text-success-400'
                  )}>
                    {result.metadata.riskLevel as string}
                  </span>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </button>
  );
}
